'use client';

import { useEffect, useState } from 'react';

import Link from 'next/link';
import { usePathname } from 'next/navigation';

import {
  ArrowLeftOutlined,
  CloseOutlined,
  MenuOutlined,
} from '@ant-design/icons';
import { Drawer } from 'antd';
import classNames from 'classnames';

import CustomButton from '@/common/components/shared/button/button.component';
import { links } from '@/common/constants/header-links';

import Logo from '~/public/assets/svg/header-logo.svg';

const MobileHeader = () => {
  const [open, setOpen] = useState(false);
  const pathname = usePathname();

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) setOpen(false);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleDrawer = () => setOpen((prev) => !prev);

  return (
    <div
      dir="rtl"
      className="flex h-16 items-center justify-between border-b border-silver-300/70 bg-white/95 px-4 backdrop-blur-xl lg:hidden"
    >
      <button
        type="button"
        onClick={toggleDrawer}
        aria-label={open ? 'بستن منو' : 'باز کردن منو'}
        aria-expanded={open}
        className="flex h-11 w-11 items-center justify-center rounded-2xl text-lg text-black-default transition-colors hover:bg-silver-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500"
      >
        {open ? <CloseOutlined /> : <MenuOutlined />}
      </button>

      <Link
        href="/"
        aria-label="ویترو - صفحه اصلی"
        className="absolute left-1/2 flex -translate-x-1/2 items-center rounded-xl p-2"
      >
        <Logo className="h-5 w-16" aria-hidden="true" />
      </Link>

      <span aria-hidden="true" className="h-11 w-11" />

      <Drawer
        open={open}
        onClose={() => setOpen(false)}
        placement="right"
        closable={false}
        styles={{
          wrapper: { width: 'min(88vw, 400px)' },
          body: { padding: 0, background: '#fdf7f4' },
          footer: {
            borderTop: '1px solid #edeceb',
            padding: '16px',
            background: '#fff',
          },
        }}
        footer={
          <CustomButton
            id="vendor-login"
            target="_blank"
            rel="noopener noreferrer"
            link={process.env.NEXT_PUBLIC_WITRO_APP_URL}
            className="w-full !rounded-xl !py-3"
            type="black"
            variant="outlined"
          >
            ورود و ثبت نام فروشندگان‌‍
          </CustomButton>
        }
      >
        <div className="flex h-18 items-center justify-between border-b border-silver-300 bg-white px-5">
          <div>
            <Logo className="h-5 w-16" aria-hidden="true" />
            <p className="mt-1 text-xs text-gray-wenge">
              فروشگاهت را ساده بساز
            </p>
          </div>
          <button
            type="button"
            aria-label="بستن منو"
            className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-50 text-lg text-cola-500 transition-colors hover:bg-primary-100"
            onClick={() => setOpen(false)}
          >
            <CloseOutlined />
          </button>
        </div>

        <nav aria-label="منوی اصلی" className="mobile-main-nav p-3">
          <ul className="flex flex-col gap-1.5">
            {links.map((item) => {
              const isActive = item.link === pathname;

              return (
                <li key={item.label}>
                  <Link
                    href={item.link || ''}
                    aria-current={isActive ? 'page' : undefined}
                    onClick={() => setOpen(false)}
                    className={classNames(
                      'relative flex min-h-12 items-center justify-between rounded-xl px-4 text-sm font-semibold transition-colors',
                      isActive
                        ? 'text-primary-600 after:absolute after:right-1 after:top-1/2 after:h-6 after:w-0.5 after:-translate-y-1/2 after:rounded-full after:bg-primary-500'
                        : '!text-black-default hover:!text-cola-500'
                    )}
                  >
                    {item.label}
                    <ArrowLeftOutlined className="text-xs text-primary-600" />
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>
      </Drawer>
    </div>
  );
};

export default MobileHeader;
